import moment from 'moment'
import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult, Ord, Paginate} from './_types'
import {AuthLoginResponse} from './AuthEndpoint'

const prefix = process.env.REACT_APP_COMPANIES_BASE_URL

type CompanyIncludes = 'permissions'

export type Company = {
  id: number
  name: string
  document: string
  apiUser?: string
  isDeleted?: boolean
  expirationDate?: string
  permissions?: Permission[]
  daysToRetainStream?: number
  daysToRetainImagens?: number
  daysToRetainEvents?: number
}

export type Permission = {
  codename: string
  scope: string
}

export type UpdatePermissions = {
  id?: number
  permissions: string[]
}

export class CreateCompany {
  name: string
  document: string
  expirationDate?: string
  daysToRetainStream?: number
  daysToRetainImagens?: number
  daysToRetainEvents?: number

  constructor(company: Company) {
    this.name = company.name
    this.document = company.document
    this.expirationDate = moment(company.expirationDate).format('YYYY-MM-DD').concat('T23:59:59Z')
    this.daysToRetainStream = company.daysToRetainStream
    this.daysToRetainImagens = company.daysToRetainImagens
    this.daysToRetainEvents = company.daysToRetainEvents
  }
}

export class UpdateCompany {
  id: number
  name?: string
  document?: string
  expirationDate?: string

  constructor(company: Company) {
    this.id = company.id
    this.name = company.name
    this.document = company.document
    this.expirationDate = moment(company.expirationDate).format('YYYY-MM-DD').concat('T23:59:59Z')
  }
}

export type CompanyCredentials = {
  apiUser: string
  apiPassword: string
}

export type GetCompaniesParams = {
  orderBy?: {
    names: Ord
  }
  page?: number
  pageSize?: number
  filter?: {
    ids?: number[]
    from?: string
  }
  search?: {
    name?: string
    document?: string
  }
  includes?: CompanyIncludes[]
}

export type SelfRegisterBody = {
  name: string
  companyName?: string
  email: string
  cpf: string
  phone: string
  password: string
  code: string
}

type SelfLogin = {
  token: AuthLoginResponse
}

export type GetUploadUrlValidationParams = {
  fileType: string
  fileName: string
}

export type FilterEdgeServersParams = {
  page?: number
  pageSize?: number
  search?: {
    clientName?: string
    id?: number
    macAddress?: string
  }
  filter?: {
    companyIds: Array<number>
  }
}

export type EdgeServersDTO = {
  id: number
  macAddress: string
  companyId?: number
  lastConnectedAt: Date
  clientId?: number
  clientName?: string
  apiUser?: string
  cameras?: number
}

export type UpdateEdgeServer = {
  apiUser?: string
  macAddress: string
  clientId?: number | null
}

export class PatchDaysToRetainCompany {
  id: number
  daysToRetainStream?: number
  daysToRetainImagens?: number
  daysToRetainEvents?: number

  constructor(id: number, client: Company) {
    this.id = id
    this.daysToRetainStream = client.daysToRetainStream
    this.daysToRetainImagens = client.daysToRetainImagens
    this.daysToRetainEvents = client.daysToRetainEvents
  }
}

export class CompanyEndpoint {
  async startMultipart({fileName, fileType}: {fileName: string; fileType: string}): Promise<AxiosResult<{uploadId: string}>> {
    return await axios.post(`${prefix}/datasets/multipart/start`, {fileName, fileType})
  }

  async getPartUploadUrl({fileName, uploadId, partNumber}: {fileName: string; uploadId: string; partNumber: number}): Promise<AxiosResult<{partUrl: string}>> {
    return await axios.get(`${prefix}/datasets/multipart/url`, {
      params: prepareParams({fileName, uploadId, partNumber}),
    })
  }

  async completeMultipartUpload(body: {
    fileName: string
    uploadId: string
    parts: {ETag: string; PartNumber: number}[]
  }): Promise<AxiosResult<{partUrl: string}>> {
    return await axios.post(`${prefix}/datasets/multipart/complete`, body)
  }

  async getMany(params: GetCompaniesParams): Promise<AxiosResult<Paginate<Company>>> {
    return await axios.get(`${prefix}/companies`, {
      params: prepareParams(params),
    })
  }

  async updatePermissions(body: UpdatePermissions): Promise<AxiosResult<UpdatePermissions>> {
    return await axios.patch(`${prefix}/companies/permissions`, body)
  }

  async create(body: CreateCompany): Promise<AxiosResult<Company>> {
    return await axios.post(`${prefix}/companies`, body)
  }

  async update(body: UpdateCompany): Promise<AxiosResult<Company>> {
    return await axios.patch(`${prefix}/companies`, body)
  }

  async delete(ids: number[]): Promise<AxiosResult<any>> {
    return await axios.delete(`${prefix}/companies`, {data: {ids}})
  }

  async selfRegister(data: SelfRegisterBody): Promise<AxiosResult<SelfLogin>> {
    return await axios.post(`${prefix}/self-register-company`, data)
  }

  async getEdgeServers(params: FilterEdgeServersParams): Promise<AxiosResult<Paginate<EdgeServersDTO>>> {
    return await axios.get(`${prefix}/edge-servers`, {
      params: prepareParams(params),
    })
  }

  async getOneEdgeServer(id: number): Promise<AxiosResult<EdgeServersDTO>> {
    return await axios.get(`${prefix}/edge-servers/${id}`)
  }

  async updateEdgeServer(body: UpdateEdgeServer): Promise<AxiosResult<any>> {
    return await axios.patch(`${prefix}/edge-servers`, body)
  }

  async updateDaysToRetain(body: PatchDaysToRetainCompany): Promise<AxiosResult<Company>> {
    return await axios.patch(`${prefix}/companies/days-to-retain`, body)
  }
}
